import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { LuX } from 'react-icons/lu';
import { tourNoPrazo, type PassoTour, type Tour } from '../config/novidades';
import { NotificacaoService } from '../services/NotificacaoService';
import { useCelular } from '../hooks/useCelular';
import './TourNovidade.css';

/**
 * Os balões de novidade dentro da tela: o ponto da mudança aceso, o resto escurecido,
 * e "Próximo" / "Entendi". Pedido dele em 17/10/2026, para o Painel Operacional.
 *
 * Quando abre — todas as condições juntas:
 * - a tela diz que está `pronta` (carregou e tem o que mostrar — empresa sem nenhuma
 *   oportunidade não vê, porque os números estão zerados e zero não abre nada);
 * - o tour está no prazo, não foi visto neste navegador nem no servidor;
 * - não é modo suporte (quem olha é a Brik; a marca seria do cliente);
 * - existe pelo menos um alvo na tela (balão sem alvo é pulado);
 * - o modal de Novidades não está aberto: abre um por vez, e o tour fica para a próxima visita.
 *
 * "Entendi", "Pular", o X e o Esc contam como visto — ninguém precisa ver de novo.
 */

const lerUsuario = () => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch { return null; }
};

const chaveLocal = (usuarioId: string, chave: string) => `lemeia:tour:${usuarioId}:${chave}`;

/** O modal de Novidades na frente (ver NovidadeModal). */
const modalDeNovidadeAberto = () => !!document.querySelector('.nvd-fundo');

const alvoVisivel = (seletor: string) => {
    const el = document.querySelector<HTMLElement>(seletor);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? el : null;
};

/** Respiro entre o elemento e a borda do recorte aceso. */
const FOLGA = 6;
const LARGURA_BALAO = 320;
const ESPACO = 12;

interface Caixa { top: number; left: number; width: number; height: number }

interface Props {
    tour: Tour;
    /** A tela carregou e tem o que mostrar. */
    pronta: boolean;
}

const TourNovidade: FC<Props> = ({ tour, pronta }) => {
    const celular = useCelular();
    const usuario = lerUsuario();
    const usuarioId = String(usuario?.id ?? usuario?.userId ?? 'anon');
    const [passos, setPassos] = useState<PassoTour[] | null>(null);
    const [i, setI] = useState(0);
    const [caixa, setCaixa] = useState<Caixa | null>(null);
    const [balao, setBalao] = useState<{ top: number; left: number } | null>(null);
    /* No celular: faixa embaixo, ou em cima quando o alvo é o fim da página e não sobe mais. */
    const [celularNoTopo, setCelularNoTopo] = useState(false);
    const refBalao = useRef<HTMLDivElement>(null);
    const refBotao = useRef<HTMLButtonElement>(null);
    const decidiu = useRef(false);

    /* As conferências, uma vez por visita à tela. */
    useEffect(() => {
        if (!pronta || decidiu.current) return;
        if (!tourNoPrazo(tour) || usuario?.emModoSuporte || usuario?.assinaturaVencida === true) return;
        try { if (localStorage.getItem(chaveLocal(usuarioId, tour.chave))) return; } catch { /* segue */ }

        let vivo = true;
        /* Um respiro depois da tela: o modal de Novidades decide aos 700 ms, e os gráficos
           medem a própria altura no primeiro quadro. */
        const t = setTimeout(() => {
            NotificacaoService.novidadesVistas().then(vistas => {
                if (!vivo || decidiu.current) return;
                decidiu.current = true;
                if (vistas?.includes(tour.chave)) {
                    try { localStorage.setItem(chaveLocal(usuarioId, tour.chave), '1'); } catch { /* segue */ }
                    return;
                }
                if (modalDeNovidadeAberto()) return;
                const comAlvo = tour.passos.filter(p => alvoVisivel(p.alvo));
                if (comAlvo.length) setPassos(comAlvo);
            });
        }, 1200);
        return () => { vivo = false; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pronta, tour, usuarioId]);

    const passo = passos?.[i] ?? null;

    /* Mede o alvo (e acompanha rolagem e janela) e decide onde o balão cabe. */
    const medir = useCallback(() => {
        if (!passo) return;
        const el = alvoVisivel(passo.alvo);
        if (!el) return;
        const r = el.getBoundingClientRect();
        const c = { top: r.top - FOLGA, left: r.left - FOLGA, width: r.width + FOLGA * 2, height: r.height + FOLGA * 2 };
        setCaixa(c);
        const altura = refBalao.current?.offsetHeight ?? 170;
        const vw = window.innerWidth, vh = window.innerHeight;
        if (celular) {
            setBalao(null);
            setCelularNoTopo(c.top + c.height > vh - altura - ESPACO * 2 && c.top > altura + ESPACO * 2);
            return;
        }
        /* Embaixo do alvo; sem espaço, em cima; sem os dois, ao lado direito; por último, sobre ele. */
        let top = c.top + c.height + ESPACO;
        let left = c.left;
        if (top + altura > vh - ESPACO) {
            if (c.top - ESPACO - altura >= ESPACO) top = c.top - ESPACO - altura;
            else if (c.left + c.width + ESPACO + LARGURA_BALAO <= vw - ESPACO) {
                top = Math.max(ESPACO, Math.min(c.top, vh - altura - ESPACO));
                left = c.left + c.width + ESPACO;
            } else top = Math.max(ESPACO, vh - altura - ESPACO);
        }
        left = Math.max(ESPACO, Math.min(left, vw - LARGURA_BALAO - ESPACO));
        setBalao({ top, left });
    }, [passo, celular]);

    /* Traz o alvo para o meio da tela antes de acender. */
    useEffect(() => {
        if (!passo) return;
        /* No celular o balão ocupa a parte de baixo: o alvo sobe para o topo (o respiro do
           cabeçalho fixo vem do scroll-margin do CSS), senão o balão cobre o bloco alto. */
        alvoVisivel(passo.alvo)?.scrollIntoView({ block: celular ? 'start' : 'center', behavior: 'smooth' });
        /* A rolagem é suave: mede de novo quando ela assenta, e só então leva o foco ao botão. */
        const t = setTimeout(() => { medir(); refBotao.current?.focus(); }, 350);
        return () => clearTimeout(t);
    }, [passo, medir, celular]);

    useLayoutEffect(() => { medir(); }, [medir]);

    useEffect(() => {
        if (!passo) return;
        const aoMudar = () => requestAnimationFrame(medir);
        window.addEventListener('resize', aoMudar);
        window.addEventListener('scroll', aoMudar, true);
        return () => {
            window.removeEventListener('resize', aoMudar);
            window.removeEventListener('scroll', aoMudar, true);
        };
    }, [passo, medir]);

    const encerrar = useCallback(() => {
        try { localStorage.setItem(chaveLocal(usuarioId, tour.chave), '1'); } catch { /* segue */ }
        /* Se a marca do servidor falhar, a do navegador segura este aqui. */
        NotificacaoService.marcarNovidadeVista(tour.chave).catch(() => {});
        setPassos(null);
    }, [tour.chave, usuarioId]);

    useEffect(() => {
        if (!passo) return;
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') encerrar(); };
        document.addEventListener('keydown', aoTeclar);
        return () => document.removeEventListener('keydown', aoTeclar);
    }, [passo, encerrar]);

    if (!passos || !passo || !caixa) return null;
    const ultimo = i === passos.length - 1;

    return createPortal(
        <div className="tn-fundo" role="presentation">
            {/* O recorte aceso: a sombra gigante é o que escurece o resto da tela. */}
            <div className="tn-foco" style={{ top: caixa.top, left: caixa.left, width: caixa.width, height: caixa.height }} />
            <div
                ref={refBalao}
                className={`tn-balao${celular ? ' is-celular' : ''}${celular && celularNoTopo ? ' is-topo' : ''}`}
                style={celular || !balao ? undefined : { top: balao.top, left: balao.left }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="tn-titulo"
                aria-describedby="tn-texto"
            >
                <div className="tn-topo">
                    <span className="tn-selo">Novidade</span>
                    <button type="button" className="tn-x" onClick={encerrar} aria-label="Fechar">
                        <LuX size={14} />
                    </button>
                </div>
                <h3 id="tn-titulo" className="tn-titulo">{passo.titulo}</h3>
                <p id="tn-texto" className="tn-texto">{passo.descricao}</p>
                <div className="tn-rodape">
                    <span className="tn-conta">{passos.length > 1 ? `${i + 1} de ${passos.length}` : ''}</span>
                    {!ultimo && <button type="button" className="tn-pular" onClick={encerrar}>Pular</button>}
                    <button
                        ref={refBotao}
                        type="button"
                        className="tn-proximo"
                        onClick={ultimo ? encerrar : () => setI(n => n + 1)}
                    >
                        {ultimo ? 'Entendi' : 'Próximo →'}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default TourNovidade;
